/**
 * Sales Manual Entry Tab Component
 * Manual entry form for covers volume data within the new tab structure
 * Extracted from SalesDataTab modal for consistent UX with ServiceConsumptionTab
 */

import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { SalesEntryConfirmationModal } from '../forms/SalesEntryConfirmationModal';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

// ============================================================================
// Types
// ============================================================================

interface SalesManualEntryFormData {
  period_start: string;
  period_end: string;
  covers_count: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const TabContainer = styled.div`
  width: 100%;
  max-width: 52rem;
  min-width: 0;
  margin: 0;
`;

const FormSection = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-md);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const Label = styled.label`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  margin-bottom: var(--space-xs);
`;

const Input = styled.input`
  padding: var(--space-md);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  transition: border-color 0.2s ease;
  background: var(--surface);
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--brand-500);
    box-shadow: 0 0 0 2px var(--brand-200);
  }

  &:disabled {
    background: var(--surface-muted);
    color: var(--text-disabled);
    cursor: not-allowed;
  }

  &[type='date'] {
    appearance: none;
  }

  &[type='number'] {
    appearance: textfield;
  }

  &[type='number']::-webkit-outer-spin-button,
  &[type='number']::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

const HelperText = styled.div`
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin-top: var(--space-xs);
`;

const DateRangeContainer = styled.div`
  display: grid;
  gap: var(--space-md);

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: var(--space-lg);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-top: var(--space-2xl);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled(Button)`
  background: var(--action-primary);
  color: white;
  border: none;
  min-width: 160px;

  &:hover {
    background: var(--action-primary-hover);
  }

  &:disabled {
    background: var(--neutral-400);
    cursor: not-allowed;
  }
`;

const ResetButton = styled(Button)`
  background: var(--surface-subtle);
  color: var(--text-secondary);
  border: 1px solid var(--border);
  min-width: 160px;

  &:hover {
    background: var(--interactive-hover);
    color: var(--text-primary);
  }
`;

// ============================================================================
// Component
// ============================================================================

export const SalesManualEntryTab: React.FC = () => {
  const [formData, setFormData] = useState<SalesManualEntryFormData>({
    period_start: '',
    period_end: '',
    covers_count: '',
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedData, setSubmittedData] =
    useState<SalesManualEntryFormData | null>(null);

  const queryClient = useQueryClient();

  // Create covers volume mutation
  const createCoversVolumeMutation = useMutation({
    mutationFn: (volumeData: {
      period_start: string;
      period_end: string;
      covers_count: number;
    }) => {
      return apiService.createCoversVolume({
        period_start: volumeData.period_start,
        period_end: volumeData.period_end,
        covers_count: volumeData.covers_count,
        source: 'manual',
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: ['dashboard', 'summary'] });
      // Invalidate analytics data since sales data affects charts and calculations
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
      toast.success(
        '¡Datos confirmados! La información se ha registrado correctamente.',
      );

      // Reset form and close modal
      setShowConfirmation(false);
      resetForm();
      setSubmittedData(null);
      console.log('✅ Covers volume created successfully:', data.id);
    },
    onError: (error: any) => {
      console.error('❌ Covers volume creation failed:', error);
      const errorMessage = error?.detail || 'Error al guardar los datos';
      toast.error(errorMessage);
      // Keep modal open on error so user can retry or edit
    },
  });

  const resetForm = () => {
    setFormData({
      period_start: '',
      period_end: '',
      covers_count: '',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.period_start ||
      !formData.period_end ||
      !formData.covers_count
    ) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const covers_count = parseInt(formData.covers_count, 10);
    if (isNaN(covers_count) || covers_count <= 0) {
      toast.error('El número de cubiertos debe ser un número positivo');
      return;
    }

    // Date validation
    if (new Date(formData.period_start) >= new Date(formData.period_end)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    // Store submitted data and show confirmation modal
    setSubmittedData(formData);
    setShowConfirmation(true);
  };

  // Handle confirmation modal actions - actually save the data here
  const handleConfirmEntry = async () => {
    if (!submittedData) return;

    const covers_count = parseInt(submittedData.covers_count, 10);
    createCoversVolumeMutation.mutate({
      period_start: submittedData.period_start,
      period_end: submittedData.period_end,
      covers_count,
    });
  };

  const handleEditEntry = () => {
    setShowConfirmation(false);
    // submittedData stays so user can continue editing
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setSubmittedData(null);
  };

  const handleReset = () => {
    resetForm();
    toast.success('Formulario reiniciado');
  };

  return (
    <TabContainer>
      <Card>
        <Form onSubmit={handleSubmit}>
          {/* Date Range Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="calendar" size="sm" />
              Período del Servicio
            </SectionTitle>
            <DateRangeContainer>
              <FormGroup>
                <Label htmlFor="period_start">
                  Fecha de Inicio del Período *
                </Label>
                <Input
                  id="period_start"
                  name="period_start"
                  type="date"
                  value={formData.period_start}
                  onChange={handleInputChange}
                  required
                />
                <HelperText>
                  Fecha cuando inició el período de servicio
                </HelperText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="period_end">Fecha de Fin del Período *</Label>
                <Input
                  id="period_end"
                  name="period_end"
                  type="date"
                  value={formData.period_end}
                  onChange={handleInputChange}
                  required
                />
                <HelperText>
                  Fecha cuando terminó el período de servicio
                </HelperText>
              </FormGroup>
            </DateRangeContainer>
          </FormSection>

          {/* Covers Count Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="dashboard" size="sm" />
              Volumen de Clientes
            </SectionTitle>
            <FormGroup>
              <Label htmlFor="covers_count">
                Número de Cubiertos/Clientes *
              </Label>
              <Input
                id="covers_count"
                name="covers_count"
                type="number"
                min="1"
                value={formData.covers_count}
                onChange={handleInputChange}
                placeholder="Ej: 150"
                required
              />
              <HelperText>
                Número total de cubiertos servidos durante este período
              </HelperText>
            </FormGroup>
          </FormSection>

          {/* Form Actions */}
          <FormActions>
            <ResetButton
              type="button"
              onClick={handleReset}
              disabled={createCoversVolumeMutation.isPending}
            >
              <Icon name="refresh" size="xs" />
              Limpiar Formulario
            </ResetButton>
            <SubmitButton
              type="submit"
              disabled={createCoversVolumeMutation.isPending}
            >
              {createCoversVolumeMutation.isPending ? (
                <>
                  <Icon name="time" size="xs" />
                  Procesando...
                </>
              ) : (
                <>
                  <Icon name="save" size="xs" />
                  Guardar Datos
                </>
              )}
            </SubmitButton>
          </FormActions>
        </Form>
      </Card>

      {/* Sales Entry Confirmation Modal */}
      {submittedData && (
        <SalesEntryConfirmationModal
          isVisible={showConfirmation}
          data={{
            period_start: submittedData.period_start,
            period_end: submittedData.period_end,
            covers_count: parseInt(submittedData.covers_count, 10),
          }}
          onConfirm={handleConfirmEntry}
          onEdit={handleEditEntry}
          onClose={handleCloseConfirmation}
          isLoading={createCoversVolumeMutation.isPending}
        />
      )}
    </TabContainer>
  );
};

SalesManualEntryTab.displayName = 'SalesManualEntryTab';
